import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Lock, Sparkles } from 'lucide-react';

// Feature descriptions for gentle toast messages
const FEATURE_MESSAGES = {
    // Support tier features
    save_favourites: {
        title: 'Save your favourites',
        description: 'Keep your most helpful grounding steps close at hand.'
    },
    extended_library: {
        title: 'Extended grounding library',
        description: 'Access a deeper collection of regulation tools.'
    },
    light_journaling: {
        title: 'Light journaling',
        description: 'Capture your feelings with gentle prompts.'
    },
    reminders: {
        title: 'Calming reminders',
        description: 'Gentle nudges to check in with yourself.'
    },
    early_overwhelm_types: {
        title: 'Early access features',
        description: 'Try new overwhelm types before anyone else.'
    },
    micro_tools: {
        title: 'Relational micro-tools',
        description: 'Quick scripts for pause, repair, and reconnection.'
    },
    // Deep Support tier features
    coregulation: {
        title: 'Partner co-regulation',
        description: 'Regulate together with someone you trust.'
    },
    shared_session: {
        title: 'Shared grounding session',
        description: 'Ground together in real-time with your partner.'
    },
    full_communication: {
        title: 'Full communication scripts',
        description: 'Complete repair scripts for relationship moments.'
    },
    full_journaling: {
        title: 'Full journaling suite',
        description: 'Deep reflection tools for your emotional journey.'
    },
    relationship_templates: {
        title: 'Relationship check-ins',
        description: 'Structured templates for connecting with your partner.'
    }
};

// Tier labels for display
const TIER_LABELS = {
    support: 'Support',
    deep_support: 'Deep Support'
};

/**
 * Hook for handling premium feature access
 * Returns a function that checks access and navigates to paywall if needed
 */
export const usePremiumFeature = () => {
    const navigate = useNavigate();
    const { canAccess, getRequiredTier } = useAuth();

    const checkAccess = (feature, onAccessGranted) => {
        if (canAccess(feature)) {
            // User has access, execute the action
            if (onAccessGranted) onAccessGranted();
            return true;
        } else {
            // User doesn't have access, show gentle message and navigate to paywall
            const message = FEATURE_MESSAGES[feature] || { 
                title: 'Premium feature', 
                description: 'Unlock more tools for your nervous system.' 
            };
            const requiredTier = getRequiredTier(feature);
            const tierLabel = TIER_LABELS[requiredTier] || 'Premium';
            
            toast.info(message.title, {
                description: message.description,
                duration: 3000,
                icon: <Sparkles className="w-4 h-4 text-[#7D8E95]" />
            });
            
            // Navigate to pricing with the required tier highlighted
            navigate('/pricing', { state: { highlightTier: requiredTier } });
            return false;
        }
    };

    return { checkAccess, canAccess };
};

/**
 * Component wrapper that shows a locked state for premium features
 * Clicking navigates to paywall
 */
export const PremiumFeatureWrapper = ({ 
    feature, 
    children, 
    className = '',
    showBadge = true,
    badgePosition = 'top-right' // top-right, top-left, inline
}) => {
    const { canAccess, getRequiredTier } = useAuth();
    const navigate = useNavigate();
    const isLocked = !canAccess(feature);
    const requiredTier = getRequiredTier(feature);
    const tierLabel = TIER_LABELS[requiredTier] || 'Premium';

    const handleClick = (e) => {
        if (isLocked) {
            e.preventDefault();
            e.stopPropagation();
            
            const message = FEATURE_MESSAGES[feature] || { 
                title: 'Premium feature', 
                description: 'Unlock more tools for your nervous system.' 
            };
            
            toast.info(message.title, {
                description: message.description,
                duration: 3000,
                icon: <Sparkles className="w-4 h-4 text-[#7D8E95]" />
            });
            
            navigate('/pricing', { state: { highlightTier: requiredTier } });
        }
    };

    const badgePositionClasses = {
        'top-right': 'absolute -top-2 -right-2',
        'top-left': 'absolute -top-2 -left-2',
        'inline': 'ml-2'
    };

    return (
        <div 
            className={`relative ${isLocked ? 'cursor-pointer' : ''} ${className}`}
            onClick={handleClick}
        >
            {children}
            {isLocked && showBadge && (
                <div className={`${badgePositionClasses[badgePosition]} flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#7D8E95]/20 border border-[#7D8E95]/30`}>
                    <Lock className="w-3 h-3 text-[#7D8E95]" />
                    <span className="text-xs font-medium text-[#7D8E95]">{tierLabel}</span>
                </div>
            )}
        </div>
    );
};

/**
 * Premium badge component for inline use
 */
export const PremiumBadge = ({ feature, size = 'sm' }) => {
    const { canAccess, getRequiredTier } = useAuth();
    const isLocked = !canAccess(feature);
    const requiredTier = getRequiredTier(feature);
    const tierLabel = TIER_LABELS[requiredTier] || 'Premium';

    if (!isLocked) return null;

    const sizeClasses = {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-sm'
    };

    return (
        <span className={`inline-flex items-center gap-1 rounded-full bg-[#7D8E95]/20 border border-[#7D8E95]/30 font-medium text-[#7D8E95] ${sizeClasses[size]}`}>
            <Sparkles className="w-3 h-3" />
            {tierLabel}
        </span>
    );
};

/**
 * Lock icon overlay for premium features
 */
export const PremiumLockIcon = ({ feature, className = '' }) => {
    const { canAccess } = useAuth();
    const isLocked = !canAccess(feature);

    if (!isLocked) return null;

    return (
        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#7D8E95] flex items-center justify-center ${className}`}>
            <Lock className="w-3 h-3 text-white" />
        </div>
    );
};

export default PremiumFeatureWrapper;
